<template>
   <NuxtLoadingIndicator />
   <CommonSnackbar></CommonSnackbar>
   <Transition name="fade" mode="out-in">
      <div :key="locale" :class="locale === 'ar' ? 'rtl' : 'ltr'" class="w-full">
         <ClientMobileAppPrompt
            v-if="mobileAppPromptState.showAppPrompt && isOnline"
            @close="closeAppPrompt"
         />
         <ClientAdConsentPrompt v-if="isOnline" />
         <div :class="isOnline ? '' : 'hidden'">
            <NuxtLayout />
         </div>
         <div
            :class="isOnline ? 'hidden' : ''"
            class="flex flex-col items-center justify-center py-10 gap-10"
         >
            <img :src="preloadedImage" />
            <div class="flex flex-col gap-2 text-center">
               <div class="text-xl font-bold">Unable to connect</div>
               <div class="text-base font-semibold text-gray-300">
                  Check your connection and reload the page
               </div>
            </div>
         </div>
      </div>
   </Transition>
</template>

<style>
   .rtl {
      direction: rtl;
   }

   .ltr {
      direction: ltr;
   }

   .fade-enter-active,
   .fade-leave-active {
      transition: opacity 0.2s ease;
   }

   .fade-enter-from,
   .fade-leave-to {
      opacity: 0.4;
   }
</style>
<script setup>
   import { getClientPromptSessionId, isClientAppPromptRoute } from '~/utils/clientMobileAppPrompt';
   const route = useRoute();
   const { isMobile } = useBrowserDetect();
   const { isClientAuthenticated, clientToken } = useAuthClientStorage();
   const { clientProfile } = storeToRefs(useClientInfo());
   const { getClientInfo } = useClientInfo();
   const { state: mobileAppPromptState, sync, closeAppPrompt } = useClientMobileAppPrompt();
   const accountReady = ref(true);
   let profileRequest = 0;
   let requestedToken = null;
   let requiresProfileRefresh = false;
   let profileLoading = false;
   watch(
      () => [clientToken.value?.access_token, route.name, isMobile.value],
      async ([token, routeName, mobile], previous = []) => {
         if (!token) {
            profileRequest++;
            requestedToken = null;
            accountReady.value = false;
            profileLoading = false;
            return;
         }
         if (previous.length && previous[0] !== token) {
            requiresProfileRefresh = true;
            accountReady.value = false;
            requestedToken = null;
         }
         if (
            (!mobile && routeName !== 'client-booking') ||
            !isClientAppPromptRoute(String(routeName || ''))
         )
            return;
         if (!requiresProfileRefresh && clientProfile.value?.id && requestedToken === null) {
            accountReady.value = true;
            requestedToken = token;
            return;
         }
         if (requestedToken === token) return;
         requestedToken = token;
         accountReady.value = false;
         const request = ++profileRequest;
         profileLoading = true;
         let loadedProfile = false;
         try {
            loadedProfile = await getClientInfo({ silent: true, expectedAccessToken: token });
         } catch {
            // The page remains usable even if the optional prompt cannot identify the account.
            return;
         } finally {
            if (request === profileRequest && clientToken.value?.access_token === token) {
               profileLoading = false;
               accountReady.value = loadedProfile;
               requiresProfileRefresh = !accountReady.value;
            }
         }
      },
      { immediate: true, flush: 'sync' },
   );
   watch(
      clientProfile,
      (profile) => {
         if (profile?.id && !profileLoading && requestedToken === clientToken.value?.access_token) {
            accountReady.value = true;
            requiresProfileRefresh = false;
         }
      },
      { flush: 'sync' },
   );
   watch(
      () => [
         route.name,
         isMobile.value,
         isClientAuthenticated.value,
         clientProfile.value?.id,
         accountReady.value,
         clientToken.value?.access_token,
      ],
      () =>
         sync({
            routeName: String(route.name || ''),
            isMobile: isMobile.value,
            isAuthenticated: isClientAuthenticated.value,
            accountId: clientProfile.value?.id || null,
            profileReady: !!clientProfile.value?.id && accountReady.value,
            sessionId: getClientPromptSessionId(clientToken.value?.access_token),
         }),
      { immediate: true, flush: 'post' },
   );
   const isOnline = ref(navigator.onLine);
   import preloadedImage from '~/public/image/no-connection.svg';
   const handleConnectionChange = () => {
      if (!navigator.onLine) isOnline.value = false;
      else isOnline.value = true;
   };

   onMounted(() => {
      window.addEventListener('online', handleConnectionChange);
      window.addEventListener('offline', handleConnectionChange);
   });

   onUnmounted(() => {
      window.removeEventListener('online', handleConnectionChange);
      window.removeEventListener('offline', handleConnectionChange);
   });
   const { locale } = useI18n();
   const { setSnackbarDetail } = useSnackbarStore();
   useHead({
      titleTemplate: (titleChunk) => {
         return titleChunk ? `${titleChunk} | Talktime ae  ` : ' Talktime ae';
      },
   });
</script>
